import React from "react";
import { Plus, Trash2 } from "lucide-react";
import type { SspComponentResponse } from "../../../../../api/frameworksApi";
import {
  FormField,
  FormInput,
  FormSelect,
  FormTextarea,
  Modal,
  ModalFooterButtons,
} from "../../../../components/Modal";
import { Tip } from "../../../../components/ui/tooltip";

export interface AddInventoryItemDialogProps {
  open: boolean;
  onClose: () => void;
  handleAddAsset: () => void;
  addingAsset: boolean;
  assetForm: {
    componentId: string;
    description: string;
    identifiers: { identifierType: string; identifierValue: string }[];
  };
  setAssetForm: React.Dispatch<
    React.SetStateAction<{
      componentId: string;
      description: string;
      identifiers: { identifierType: string; identifierValue: string }[];
    }>
  >;
  sspComponents: SspComponentResponse[];
}

export function AddInventoryItemDialog({
  open,
  onClose,
  handleAddAsset,
  addingAsset,
  assetForm,
  setAssetForm,
  sspComponents,
}: AddInventoryItemDialogProps) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add Inventory Item"
      description="Add a new asset to the system inventory"
      size="md"
      footer={
        <ModalFooterButtons
          onCancel={onClose}
          onSubmit={handleAddAsset}
          submitLabel={addingAsset ? "Saving" : "Save"}
          submitDisabled={
            !assetForm.componentId || !assetForm.description || addingAsset
          }
        />
      }
    >
      {sspComponents.length === 0 ? (
        <div className="bg-yellow-50 dark:bg-yellow-500/10 border border-yellow-300 dark:border-yellow-500/30 text-yellow-800 dark:text-yellow-300 rounded-md p-3 text-sm">
          No components found. Please create a Component first before adding
          Inventory Items.
        </div>
      ) : (
        <FormField label="Component" required>
          <FormSelect
            options={sspComponents.map((c) => ({
              label: `${c.title} (${c.componentType})`,
              value: c.id,
            }))}
            placeholder="Select a component"
            value={assetForm.componentId}
            onChange={(v) => setAssetForm((f) => ({ ...f, componentId: v }))}
          />
        </FormField>
      )}
      <FormField label="Description" required>
        <FormTextarea
          placeholder="e.g. Production PostgreSQL RDS Instance"
          value={assetForm.description}
          onChange={(v) => setAssetForm((f) => ({ ...f, description: v }))}
          rows={2}
        />
      </FormField>
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium">Asset Identifiers</label>
          <button
            type="button"
            onClick={() =>
              setAssetForm((f) => ({
                ...f,
                identifiers: [
                  ...f.identifiers,
                  { identifierType: "", identifierValue: "" },
                ],
              }))
            }
            className="flex items-center gap-1 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors"
          >
            <Plus className="size-3" />
            Add Identifier
          </button>
        </div>
        {assetForm.identifiers.map((ident, idx) => (
          <div key={idx} className="flex items-start gap-2">
            <div className="grid grid-cols-2 gap-2 flex-1">
              <FormSelect
                options={[
                  { value: "hostname", label: "Hostname" },
                  { value: "ip_address", label: "IP Address" },
                  { value: "ipv4-address", label: "IPv4 Address" },
                  { value: "ipv6-address", label: "IPv6 Address" },
                  { value: "fqdn", label: "FQDN" },
                  { value: "mac-address", label: "MAC Address" },
                  { value: "serial-number", label: "Serial Number" },
                  { value: "cloud-arn", label: "Cloud ARN" },
                  { value: "container-id", label: "Container ID" },
                  { value: "uri", label: "URI" },
                  { value: "asset-id", label: "Asset ID" },
                  { value: "other", label: "Other" },
                ]}
                placeholder="Select type"
                value={ident.identifierType}
                onChange={(v) =>
                  setAssetForm((f) => ({
                    ...f,
                    identifiers: f.identifiers.map((id, i) =>
                      i === idx ? { ...id, identifierType: v } : id,
                    ),
                  }))
                }
              />
              <FormInput
                placeholder="e.g. web-prod-01.example.com"
                value={ident.identifierValue}
                onChange={(v) =>
                  setAssetForm((f) => ({
                    ...f,
                    identifiers: f.identifiers.map((id, i) =>
                      i === idx ? { ...id, identifierValue: v } : id,
                    ),
                  }))
                }
              />
            </div>
            {assetForm.identifiers.length > 1 && (
              <Tip label="Remove Identifier" side="bottom">
                <button
                  type="button"
                  onClick={() =>
                    setAssetForm((f) => ({
                      ...f,
                      identifiers: f.identifiers.filter((_, i) => i !== idx),
                    }))
                  }
                  className="mt-1.5 p-1.5 rounded-md text-muted-foreground hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors flex-shrink-0"
                >
                  <Trash2 className="size-3.5" />
                </button>
              </Tip>
            )}
          </div>
        ))}
      </div>
    </Modal>
  );
}
